import React from 'react';
import { AppDomain } from '@pegasus/config';
import { PlusBenefit } from '@pegasus/plus';
import { LanguageCode, Translate, TranslateScope } from '@pegasus/translation';
import { ErrorBoundary } from '@browsing/components';
import { GridPageLayout } from '@lux/grid';
import { Tag, Title, TitlePreset } from '@lux/title';
import { UPCOMING_CAMPAIGNS_WRAPPER } from '../../constants';
import { EAUpcomingCarousel } from '../../plus/ea-upcoming-carousel';

/**
 * This is a manual snapshot of
 * https://github.com/zalando-lounge/modules/blob/master/packages/upcoming-section/src/upcoming-section.tsx#L17
 * to allow readability of section wrapper without impacting the bundle size of the UpcomingSection component.
 */
export interface UpcomingCampaignType {
    name: string;
    campaignId: string;
    startsAt: string;
    media: {
        url: string;
        altText?: string;
    };
    hasLogo: boolean;
    /**
     * This is a new field that replaces the deprecated plusEarlyAccess and plusEndsAt fields.
     * @experimental
     */
    plusBenefit?: PlusBenefit;
}

/**
 * This is a manual snapshot of
 * https://github.com/zalando-lounge/modules/blob/master/packages/upcoming-section/src/upcoming-section.tsx#L17
 * to allow readability of section wrapper without impacting the bundle size of the UpcomingSection component.
 */
export interface UpcomingSectionProps {
    hasTitle?: boolean;
    titleTag?: Tag;
    isPlusEnabled?: boolean;
    isPlusExclusiveEnabled?: boolean;
    upcomingCampaigns: UpcomingCampaignType[];
    showNewsletterImage?: boolean;
    appDomain: AppDomain;
    locale: LanguageCode;
    campaignIdToHighlight?: string;
    horizontal?: boolean;
    onCountdownFinish: (message: string, callbackText: string) => void;
    customHighlightSection?: React.ReactNode;
    toggleSubscription: (campaignId: string) => void;
    subscribedCampaigns: Record<string, boolean>;
}

type InternalUpcomingSectionProps = {
    showEAUpcomingCarousel: boolean;
    UpcomingSectionComponent: React.ComponentType<UpcomingSectionProps>;
    upcomingSectionProps: UpcomingSectionProps;
};

export const InternalUpcomingSection: React.FC<InternalUpcomingSectionProps> = ({
    showEAUpcomingCarousel,
    UpcomingSectionComponent,
    upcomingSectionProps,
}) => (
    <ErrorBoundary componentName="UpcomingSection">
        <GridPageLayout>
            <TranslateScope path="upcoming">
                <Title className="mt-xl mb-m px-s sm:p-0" preset={TitlePreset.S} tag={Tag.H2}>
                    <Translate message="upcomingCampaigns.upcomingTitle" />
                </Title>
            </TranslateScope>
        </GridPageLayout>
        <GridPageLayout>
            <section
                className="bg-background-mylounge pb-l relative"
                id={UPCOMING_CAMPAIGNS_WRAPPER}
                data-testid={UPCOMING_CAMPAIGNS_WRAPPER}
            >
                {showEAUpcomingCarousel && <EAUpcomingCarousel />}
                <UpcomingSectionComponent {...upcomingSectionProps} />
            </section>
        </GridPageLayout>
    </ErrorBoundary>
);
